import { useNavigate } from "react-router-dom";
import { useUser } from "../../auth/UserContext.js";
import { Icon } from "../../components/Icon.js";
import { GoalMini } from "../../components/GoalMini.js";
import { TxnRow } from "../../components/TxnRow.js";
import { fmt } from "../../lib/money.js";
import type { KidBundle } from "../../api/me.js";

export default function Home() {
  const { bundle } = useUser();
  const navigate = useNavigate();
  if (!bundle || bundle.user.role !== "kid") return null;
  const kid = bundle as KidBundle;

  const color = kid.user.color ?? "var(--brand)";
  const colorSoft = kid.user.colorSoft ?? "var(--brand-soft)";

  const annualRate = kid.settings.interestRateAnnualBasisPoints / 10000;
  const monthlyRate = annualRate / 12;
  const monthInterestCents = Math.round(kid.account.balanceCents * monthlyRate);

  const allocatedToGoals = kid.goals.reduce((s, g) => s + g.savedCents, 0);
  const unallocated = kid.account.balanceCents - allocatedToGoals;

  // Compute pending balance (what balance would be if all pending transfers approved)
  const pendingAdjustment = (kid.pendingTransfers ?? []).reduce((s, p) => {
    if (p.fromKidId === kid.user.rowKey) return s - p.amountCents;
    if (p.toKidId === kid.user.rowKey) return s + p.amountCents;
    return s;
  }, 0);
  const hasPending = pendingAdjustment !== 0;
  const pendingBalanceCents = kid.account.balanceCents + pendingAdjustment;

  const activeBills = (kid.bills ?? []).filter((b) => b.monthsRemaining > 0);
  const totalBillsCents = activeBills.reduce((s, b) => s + b.amountCents, 0);
  const netAllowanceCents = kid.account.allowanceCents - totalBillsCents;

  const recent = (kid.recentTxns ?? []).slice(0, 5);
  const topGoals = (kid.goals ?? []).slice(0, 3);

  const nextPaydayDays = (() => {
    const d = new Date();
    const next = new Date(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
    return Math.ceil((next.getTime() - d.getTime()) / 86_400_000);
  })();
  const totalDaysInMonth = new Date(
    new Date().getUTCFullYear(),
    new Date().getUTCMonth() + 1,
    0,
  ).getUTCDate();
  const elapsedDays = Math.max(0, totalDaysInMonth - nextPaydayDays);
  const paydayPct = totalDaysInMonth > 0 ? (elapsedDays / totalDaysInMonth) * 100 : 0;

  return (
    <div className="hero-grid">
      {/* Balance hero */}
      <div
        className="card raised"
        style={{
          position: "relative",
          overflow: "hidden",
          padding: 36,
          background: `linear-gradient(135deg, ${colorSoft} 0%, white 60%)`,
        }}
      >
        <div
          className="decoration"
          style={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: color,
            opacity: 0.12,
          }}
        />
        <div
          className="decoration"
          style={{
            position: "absolute",
            top: 40,
            right: 40,
            width: 80,
            height: 80,
            borderRadius: "50%",
            border: `3px solid ${color}`,
            opacity: 0.25,
          }}
        />

        <div style={{ position: "relative" }}>
          <div className="soft" style={{ fontSize: 14, marginBottom: 10 }}>
            Your total balance
          </div>
          <div className="money money-xl" style={{ marginBottom: 16 }}>
            {fmt(kid.account.balanceCents)}
          </div>

          {hasPending && (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "oklch(0.97 0.02 80)",
                border: "1px solid oklch(0.88 0.06 80)",
                borderRadius: 8,
                padding: "4px 10px",
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              <Icon name="transfer" size={14} color="oklch(0.55 0.12 80)" />
              <span style={{ color: "oklch(0.45 0.1 80)" }}>
                Pending balance: <span style={{ fontWeight: 600 }}>{fmt(pendingBalanceCents)}</span>
              </span>
            </div>
          )}

          <div style={{ display: "flex", gap: 24, marginTop: hasPending ? 8 : 20 }}>
            <div>
              <div className="soft" style={{ fontSize: 12, marginBottom: 2 }}>
                Free to spend
              </div>
              <div className="money money-md">{fmt(unallocated)}</div>
            </div>
            <div className="divider-v" />
            <div>
              <div className="soft" style={{ fontSize: 12, marginBottom: 2 }}>
                Saved toward goals
              </div>
              <div className="money money-md">{fmt(allocatedToGoals)}</div>
            </div>
          </div>

          <div style={{ marginTop: 22, display: "flex", gap: 8 }}>
            <div className="chip brand">
              <Icon name="sparkle" size={12} />
              Earning {(annualRate * 100).toFixed(1)}% a year
            </div>
            <div className="chip">+{fmt(monthInterestCents)} this month</div>
          </div>
        </div>
      </div>

      {/* Quick actions + next payday */}
      <div className="stack" style={{ gap: 16 }}>
        <div className="card">
          <div style={{ fontSize: 13, color: "var(--ink-mute)", marginBottom: 4 }}>
            Next allowance
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
            <div className="money money-lg">{fmt(netAllowanceCents)}</div>
            <div className="soft" style={{ fontSize: 13 }}>
              in {nextPaydayDays} {nextPaydayDays === 1 ? "day" : "days"}
            </div>
          </div>
          {totalBillsCents > 0 && (
            <div className="soft" style={{ fontSize: 12, marginBottom: 8 }}>
              {fmt(kid.account.allowanceCents)} base − {fmt(totalBillsCents)} in bills
            </div>
          )}
          <div className="progress">
            <div className="progress-fill" style={{ width: `${paydayPct}%`, background: color }} />
          </div>
          <div className="soft" style={{ fontSize: 12, marginTop: 8 }}>
            Paid on the 1st of every month
          </div>
        </div>

        <div className="card">
          <div style={{ fontSize: 13, color: "var(--ink-mute)", marginBottom: 10 }}>
            Quick actions
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <button className="btn btn-soft" onClick={() => navigate("/kid/goals")}>
              <Icon name="target" size={16} /> Goals
            </button>
            <button className="btn btn-soft" onClick={() => navigate("/kid/allowance")}>
              <Icon name="piggy" size={16} /> Allocate
            </button>
            <button className="btn btn-soft" onClick={() => navigate("/kid/history")}>
              <Icon name="history" size={16} /> History
            </button>
            <button className="btn btn-soft" onClick={() => navigate("/kid/learn")}>
              <Icon name="sparkle" size={16} /> Learn
            </button>
          </div>
        </div>
      </div>

      {/* Goals mini grid */}
      <div className="card" style={{ gridColumn: "1 / -1" }}>
        <div className="row-between" style={{ marginBottom: 16 }}>
          <h3>Your goals</h3>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate("/kid/goals")}>
            See all <Icon name="chevronRight" size={14} />
          </button>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${Math.max(1, Math.min(topGoals.length, 3))}, 1fr)`,
            gap: 16,
          }}
        >
          {topGoals.map((g) => (
            <GoalMini
              key={g.rowKey}
              goal={g}
              kid={kid.user}
              account={kid.account}
              onClick={() => navigate(`/kid/goals/${g.rowKey}`)}
            />
          ))}
        </div>
      </div>

      {/* Recent activity */}
      <div className="card" style={{ gridColumn: "1 / -1" }}>
        <div className="row-between" style={{ marginBottom: 8 }}>
          <h3>Recent activity</h3>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate("/kid/history")}>
            See all <Icon name="chevronRight" size={14} />
          </button>
        </div>
        <div>
          {recent.map((t) => (
            <TxnRow key={t.rowKey} t={t} />
          ))}
        </div>
      </div>
    </div>
  );
}
